<!--
  @component CollectionContextMenu
  @description Context menu for collection actions
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { t } from '@/i18n';
  import {
    collectionContextMenu,
    hideCollectionContextMenu,
    showCreateCollectionModal,
    showRenameCollectionModal,
  } from '@stores/uiStore';
  import { selectedLibraryId, selectedLibraryType } from '@stores/libraryStore';
  import { collectionActions } from '@services/collectionActionsService';
  import { enableMenuKeyboardNavigation, createEscapeListener } from '@/utils';
  import type { Collection } from '@types';

  let menuElement: HTMLDivElement;
  let cleanupKeyboardNav: (() => void) | null = null;

  // Centralized keyboard handling for Escape key
  const escapeHandler = createEscapeListener(hideCollectionContextMenu);

  // Handle menu action
  async function handleAction(action: string) {
    const collection = $collectionContextMenu.collection;
    const isSpecial = $collectionContextMenu.isSpecial;

    switch (action) {
      case 'createSubCollection':
        if (collection && !isSpecial) {
          showCreateCollectionModal(collection as Collection);
        }
        break;
      case 'createSibling':
        showCreateCollectionModal();
        break;
      case 'rename':
        if (collection && !isSpecial) {
          showRenameCollectionModal(collection as Collection);
        }
        break;
      case 'duplicate':
        // TODO: Implement duplicate collection
        break;
      case 'export':
        // TODO: Implement export collection
        break;
      case 'delete':
        if (collection && !isSpecial && $selectedLibraryId && $selectedLibraryType) {
          if (confirm($t('collection.confirmDeleteMessage'))) {
            await collectionActions.deleteCollection(
              $selectedLibraryId,
              $selectedLibraryType,
              collection as Collection
            );
          }
        }
        break;
      case 'emptyTrash':
        if ($selectedLibraryId && $selectedLibraryType) {
          if (confirm($t('collection.emptyTrashConfirm'))) {
            await collectionActions.emptyTrash($selectedLibraryId, $selectedLibraryType);
          }
        }
        break;
      case 'refresh':
        if ($selectedLibraryId && $selectedLibraryType) {
          await collectionActions.refreshCollections($selectedLibraryId, $selectedLibraryType);
        }
        break;
    }

    hideCollectionContextMenu();
  }

  // Adjust position to stay in viewport
  function adjustPosition() {
    if (!menuElement || !$collectionContextMenu.visible) return;

    const rect = menuElement.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    let x = $collectionContextMenu.x;
    let y = $collectionContextMenu.y;

    if (x + rect.width > windowWidth) {
      x = windowWidth - rect.width - 10;
    }

    if (y + rect.height > windowHeight) {
      y = windowHeight - rect.height - 10;
    }

    menuElement.style.left = `${x}px`;
    menuElement.style.top = `${y}px`;
  }

  onMount(() => {
    escapeHandler.attach();
    adjustPosition();
  });

  onDestroy(() => {
    escapeHandler.detach();
    cleanupKeyboardNav?.();
  });

  $: if ($collectionContextMenu.visible && menuElement) {
    requestAnimationFrame(() => {
      adjustPosition();
      cleanupKeyboardNav?.();
      cleanupKeyboardNav = enableMenuKeyboardNavigation(menuElement, '[role="menuitem"]');
    });
  }
</script>

{#if $collectionContextMenu.visible && $collectionContextMenu.collection}
  <div
    class="context-menu"
    bind:this={menuElement}
    style="left: {$collectionContextMenu.x}px; top: {$collectionContextMenu.y}px;"
    role="menu"
    aria-label={$t('a11y.collectionActions')}
    tabindex="-1"
  >
    {#if !$collectionContextMenu.isSpecial}
      <!-- Regular Collection Actions -->
      <button
        class="context-menu-item"
        on:click={() => handleAction('createSubCollection')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">📁</span>
        <span class="menu-text">{$t('collection.newSubcollection')}</span>
      </button>

      <div class="context-menu-separator" role="separator"></div>

      <button
        class="context-menu-item"
        on:click={() => handleAction('rename')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">✏️</span>
        <span class="menu-text">{$t('collection.renameCollection')}</span>
      </button>

      <button
        class="context-menu-item"
        on:click={() => handleAction('duplicate')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">📋</span>
        <span class="menu-text">{$t('common.duplicate')}</span>
      </button>

      <div class="context-menu-separator" role="separator"></div>

      <button
        class="context-menu-item"
        on:click={() => handleAction('export')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">📤</span>
        <span class="menu-text">{$t('common.export')}</span>
      </button>

      <div class="context-menu-separator" role="separator"></div>

      <button
        class="context-menu-item danger"
        on:click={() => handleAction('delete')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">🗑️</span>
        <span class="menu-text">{$t('collection.deleteCollection')}</span>
      </button>

      <div class="context-menu-separator" role="separator"></div>

      <button
        class="context-menu-item"
        on:click={() => handleAction('createSibling')}
        role="menuitem"
        tabindex="-1"
      >
        <span class="menu-icon" aria-hidden="true">➕</span>
        <span class="menu-text">{$t('collection.newCollection')}</span>
      </button>
    {:else}
      <!-- Special Collection Actions -->
      {#if $collectionContextMenu.collection.key === 'trash'}
        <button
          class="context-menu-item"
          on:click={() => handleAction('emptyTrash')}
          role="menuitem"
          tabindex="-1"
        >
          <span class="menu-icon" aria-hidden="true">🧹</span>
          <span class="menu-text">{$t('collection.emptyTrash')}</span>
        </button>
      {:else}
        <button
          class="context-menu-item"
          on:click={() => handleAction('refresh')}
          role="menuitem"
          tabindex="-1"
        >
          <span class="menu-icon" aria-hidden="true">🔄</span>
          <span class="menu-text">{$t('common.refresh')}</span>
        </button>
      {/if}
    {/if}
  </div>
{/if}

<style>
  .context-menu {
    position: fixed;
    background: var(--color-bg-secondary, #383838);
    border: 1px solid var(--color-border, #555);
    border-radius: 6px;
    box-shadow:
      0 4px 12px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    z-index: 3000;
    min-width: 220px;
    padding: 4px 0;
    font-size: 13px;
    user-select: none;
    animation: contextMenuAppear 0.15s ease-out;
  }

  .context-menu-item {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    gap: 10px;
    color: var(--color-text-primary, #e8e8e8);
    width: 100%;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 13px;
  }

  .context-menu-item:hover,
  .context-menu-item:focus {
    background: var(--color-bg-hover, #4a4a4a);
    outline: none;
  }

  .context-menu-item:focus-visible {
    background: var(--color-bg-hover, #4a4a4a);
    outline: 2px solid var(--color-accent, #5c7cfa);
    outline-offset: -2px;
  }

  .context-menu-item.danger {
    color: var(--color-danger, #ff6b6b);
  }

  .context-menu-item.danger:hover,
  .context-menu-item.danger:focus {
    background: rgba(255, 107, 107, 0.1);
  }

  .context-menu-item.danger:focus-visible {
    outline: 2px solid var(--color-danger, #ff6b6b);
  }

  .menu-icon {
    font-size: 14px;
    width: 18px;
    text-align: center;
    flex-shrink: 0;
  }

  .menu-text {
    flex: 1;
    font-weight: 500;
  }

  .context-menu-separator {
    height: 1px;
    background: var(--color-border, #555);
    margin: 6px 10px;
  }

  @keyframes contextMenuAppear {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-4px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
</style>
